import { ReactNode, useEffect, useState, useCallback } from 'react'
import { GrowthBook, GrowthBookProvider, BrowserCookieStickyBucketService } from '@growthbook/growthbook-react'
import { useAuthStore } from '@hotmart/hub-store'
import { sendAnalyticsEvent } from '@hotmart/hub-components'
import Cookies from 'js-cookie'

interface IGrowthBook {
  children: ReactNode
}

const growthbook = new GrowthBook({
  apiHost: process.env.GROWTHBOOK_API_HOST,
  clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
  enableDevMode: process.env.NODE_ENV !== 'production',
  debug: process.env.NODE_ENV === 'development',
  stickyBucketService: new BrowserCookieStickyBucketService({
    jsCookie: Cookies,
    cookieAttributes: {
      expires: 60,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'Lax'
    }
  }),
  trackingCallback: (experiment, result) => {
    try {
      const eventPayload = {
        event: 'experiment_viewed',
        experiment_id: experiment.key,
        variation_id: result.key,
        userID: growthbook.getAttributes().id
      }
      sendAnalyticsEvent(eventPayload)
    } catch (error: unknown) {
      console.error('Error pushing data to GTM:', error)
    }
  }
})

const GrowthBookInitializer = ({ children }: IGrowthBook) => {
  const [user] = useAuthStore()
  const userId = user?.profile?.id
  const userLocale = user?.profile?.locale || navigator.language
  const [isInitialized, setIsInitialized] = useState(false)
  const authorities = user?.profile?.authorities
  const CREATOR_AUTHORITY = 'produtor'
  const isCreator = authorities?.includes(CREATOR_AUTHORITY) || false

  const setGrowthBookAttributes = useCallback(() => {
    if (!userId) return

    growthbook.setAttributes({
      id: userId,
      locale: userLocale,
      path: window.location.pathname,
      query: window.location.search,
      browser: navigator.userAgent,
      host: window.location.host,
      deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
    })
  }, [userId, userLocale])

  const initializeGrowthBook = useCallback(async () => {
    try {
      await growthbook.init({ streaming: true })
      setIsInitialized(true)
    } catch (error: unknown) {
      console.error('GrowthBook initialization error:', error)
    }
  }, [])

  useEffect(() => {
    if (!userId || isCreator) return

    setGrowthBookAttributes()
    initializeGrowthBook()
  }, [userId, isCreator, setGrowthBookAttributes, initializeGrowthBook])

  useEffect(() => {
    if (!isInitialized) return
  }, [])

  return <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
}

export default GrowthBookInitializer
